import { ChangeDetectionStrategy, Component, inject, signal, WritableSignal } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { TableComponent } from "@shared/components/table/table.component";
import { TableDataGroupComponent } from "@shared/components/table/components/table-data-group/table-data-group.component";
import { TableHeaderGroupComponent } from "@shared/components/table/components/table-header-group/table-header-group.component";
import { TableHeaderRowComponent } from "@shared/components/table/components/table-header-row/table-header-row.component";
import { TableHeaderComponent } from "@shared/components/table/components/table-header/table-header.component";
import { TableDataRowComponent } from "@shared/components/table/components/table-data-row/table-data-row.component";
import { TableDataComponent } from "@shared/components/table/components/table-data/table-data.component";
import { TableAccordionDirective } from '@shared/components/table-implementation/directives/table-accordion/table-accordion.directive';
import { MatButtonModule } from "@angular/material/button";
import { IconModule } from "@ogs-gmbh/ngx-m3-components";
import { FaIconLibrary } from '@fortawesome/angular-fontawesome';
import { faEye, faEyeSlash, faRotateRight } from '@fortawesome/pro-light-svg-icons';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { ProjectStaffDataService } from './services/project-staff-data.service';
import { ProjectStaffFields, ProjectStaffOptions } from './types/project-staff-data.type';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NgTemplateOutlet } from '@angular/common';
import { TableSkeletonComponent } from "@shared/components/table/components/table-skeleton/table-skeleton.component";
import { provideTranslationScope, TranslationPipeModule } from '@ogs-gmbh/ngx-translate';
import { FormFooterComponent } from "../../../../../shared/components/form-footer/form-footer.component";
import { FormStateService } from '@core/services/form-state.service';
import { ComponentToastService } from '@shared/services/component-toast/component-toast.service';
import { ThrottleClickDirective } from "@shared/directives/throttle-click.directive";
import { MatDialog } from '@angular/material/dialog';
import { ProjectStaffDataResetDialogComponent } from './components/project-staff-data-reset-dialog/project-staff-data-reset-dialog.component';
import { OverviewComponent } from '@shared/components/templates/overview/overview.component';
import { STATUS_TYPES } from '@shared/types/status.types';


const TRANSLATION_SCOPE: string = "feature/project-staff-data";

/**
 * Gets the direct keys of a given object
 *
 * @param obj - the object of which the keys should be returned
 * @returns the typesafe keys of the given object
 */
export function keys<T extends object> (obj: T): ReadonlyArray<keyof T> {
  const _keys: Array<keyof T> = [];

  for (const key in obj) {
    if (Object.hasOwn(obj, key))
      _keys.push(key);
  }

  return _keys;
}
@Component({
  selector: 'app-project-staff-data-overview',
  standalone: true,
  imports: [ MatExpansionModule,
    TableComponent,
    TableDataGroupComponent,
    TableHeaderGroupComponent,
    TableHeaderRowComponent,
    TableHeaderComponent,
    TableDataRowComponent,
    TableDataComponent,
    TableAccordionDirective,
    MatButtonModule,
    IconModule,
    MatCheckboxModule,
    NgTemplateOutlet,
    TableSkeletonComponent,
    TranslationPipeModule,
    FormFooterComponent,
    ThrottleClickDirective,
    OverviewComponent ],
  providers: [
    provideTranslationScope(TRANSLATION_SCOPE),
    ProjectStaffDataService
  ],
  templateUrl: './project-staff-data-overview.component.html',
  styleUrl: './project-staff-data-overview.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProjectStaffDataOverviewComponent {
  private readonly _faIconLibrary: FaIconLibrary = inject(FaIconLibrary);

  private readonly _formStateService: FormStateService = inject(FormStateService);

  private readonly _projectStaffDataService: ProjectStaffDataService = inject(ProjectStaffDataService);

  private readonly _toastService: ComponentToastService = inject(ComponentToastService);

  private readonly _dialog: MatDialog = inject(MatDialog);

  // eslint-disable-next-line @unicorn/no-useless-undefined
  protected readonly projectStaffData: WritableSignal<ProjectStaffFields | undefined> = signal(undefined);

  constructor () {
    this._faIconLibrary.addIcons(faEye, faEyeSlash, faRotateRight);
    this._projectStaffDataService.data$
      .pipe(
        takeUntilDestroyed()
      ).subscribe((staffData: ProjectStaffFields) => {
        this.projectStaffData.set(staffData);
        this._formStateService.clear();
      });
  }

  protected setVisibility (staffField: ProjectStaffOptions, isVisible: boolean): void {
    if (!staffField.isRequired)
      this.updateStaffData({ ...staffField, isVisible });
  }

  protected setRequirement (staffField: ProjectStaffOptions, isRequired: boolean): void {
    const isVisible: boolean = isRequired ? true : staffField.isVisible;

    this.updateStaffData({ ...staffField, isRequired, isVisible });
  }

  private updateStaffData (staffField: ProjectStaffOptions): void {
    const currentStaffData: ProjectStaffFields | undefined = this.projectStaffData();

    if (currentStaffData) {
      const property: keyof ProjectStaffFields | undefined = keys(currentStaffData).find((key: keyof ProjectStaffFields) => currentStaffData[ key ].field === staffField.field);

      if (!property) return;

      this.projectStaffData.update((current: ProjectStaffFields | undefined) => {
        if (!current) return current;

        this._formStateService.setValidity(true);
        this._formStateService.markDirty();

        return {
          ...current,
          [ property ]: {
            ...staffField
          }
        };
      });
    }
  }

  protected reset (): void {
    this._dialog.open(ProjectStaffDataResetDialogComponent)
      .afterClosed()
      .subscribe((confirmed: boolean): void => {
        if (confirmed)
          this._projectStaffDataService.reset().subscribe();
      });
  }

  protected discardChanges (): void {
    this._projectStaffDataService.reload();
  }

  protected saveChanges (): void {
    const staffData: ProjectStaffFields | undefined = this.projectStaffData();

    if (staffData) {
      this._projectStaffDataService.update(staffData)
        .subscribe({
          next: () => {
            this._toastService.showToast(STATUS_TYPES.success, {
              defaultText: "Personaldaten wurden erfolgreich aktualisiert",
              token: "status-staff-data-updated-successfully",
              scope: TRANSLATION_SCOPE
            });
          },
          error: () => {
            this._toastService.showToast(STATUS_TYPES.error, {
              defaultText: "Fehler beim Aktualisieren der Personaldaten",
              token: "status-staff-data-update-error",
              scope: TRANSLATION_SCOPE
            });
          }
        });
    }
  }
}
